// Block break time (PLR-2). PURE (ENG-1).
//
// `speed = toolBase (if the tool class matches) ; …enchant/haste/fatigue/water/
// air multipliers` (all 1 until those systems land). `damage/tick = speed /
// hardness / 30` with the correct tool tier, else `/100`; the block breaks once
// the accumulated damage reaches 1. Hardness < 0 is unbreakable; hardness 0
// breaks instantly (E-13); creative is always instant.
import { TOOLS } from '../config';
import type { Registries } from '../registry/load';
import { type HeldTool, toolHarvests } from './drops';
import { efficiencyBonus, enchantLevelOf } from './enchant_effects';

export interface BreakOpts {
  /** Creative / instant-break mode. */
  instant?: boolean;
  /** Digging while airborne quarters the speed (MC). */
  onGround?: boolean;
  /** Digging while submerged (no aqua affinity) quarters the speed. */
  inWater?: boolean;
  /** Haste/Mining-Fatigue dig-speed multiplier (game/effects digSpeedMultiplier). */
  digSpeedMultiplier?: number;
}

/** Game ticks to break `state` with `tool`. Infinity = unbreakable, 0 = instant. */
export function breakTicks(reg: Registries, state: number, tool: HeldTool | null, opts: BreakOpts = {}): number {
  const name = reg.states.blockName[state];
  const def = name ? reg.blocks.byName.get(name) : undefined;
  if (!def) return 0;
  if (def.hardness < 0) return Infinity; // bedrock / unbreakable
  if (opts.instant) return 0;
  if (def.hardness === 0) return 1; // torches, plants (E-13)

  const matches = !!tool?.toolClass && tool.toolClass === def.toolClass;
  let speed = matches ? TOOLS.SPEED[tool!.toolTier ?? ''] ?? 1 : 1;
  // Efficiency adds level²+1 to the speed of a matching tool (Java §mining).
  if (matches) speed += efficiencyBonus(enchantLevelOf(tool!.enchants, 'efficiency'));
  if (opts.inWater) speed *= 0.2;
  if (opts.onGround === false) speed *= 0.2;
  if (opts.digSpeedMultiplier !== undefined) speed *= opts.digSpeedMultiplier; // haste / mining fatigue

  const canHarvest = toolHarvests(reg, state, tool);
  const div = canHarvest ? TOOLS.DIV_CORRECT : TOOLS.DIV_WRONG;
  const dmgPerTick = speed / def.hardness / div;
  return Math.max(1, Math.ceil(1 / dmgPerTick));
}

/** Convenience: break time in seconds (20 TPS). */
export function breakSeconds(reg: Registries, state: number, tool: HeldTool | null, opts: BreakOpts = {}): number {
  const t = breakTicks(reg, state, tool, opts);
  return t === Infinity ? Infinity : t / 20;
}
